import { type PortableTextBlock } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import { Callout } from '@/app/components/Callout'
import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'
import { Button, Callout as CalloutType, FaqTeaser as FaqTeaserProps, Link } from '@/sanity.types'

// Explicitly define the dereferenced FAQ item type (matches query result structure)
type DereferencedFaqItem = {
  _key?: string
  calloutLabel?: string | null
  item?: {
    _id: string
    language: 'en' | 'es'
    question: PortableTextBlock[]
    answer: PortableTextBlock[]
  } | null
}

type FaqTeaserWithDereferencedItems = Omit<FaqTeaserProps, 'faqItems'> & {
  faqItems?: DereferencedFaqItem[]
}

type DereferencedLink = Omit<Link, 'internalPage' | 'internalPost'> & {
  internalPage?: {
    _id: string
    slug: string
    name: string
  }
  href?: string
}

type DereferencedButton = Omit<Button, 'link'> & {
  link: DereferencedLink
}

type DereferencedCallout = Omit<CalloutType, 'button'> & {
  button?: DereferencedButton
}

type FaqProps = {
  block: FaqTeaserWithDereferencedItems & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

const FAQ_PAGES_LINK: Record<'en' | 'es', DereferencedLink> = {
  en: {
    _type: 'link',
    linkType: 'internal',
    href: '/support',
    label: 'See more',
  },
  es: {
    _type: 'link',
    linkType: 'internal',
    href: '/es/asistencia',
    label: 'ver más',
  },
}

export default function FaqTeaser({ block, pageId, pageType }: FaqProps) {
  return (
    <div className="content-container my-7xl lg:my-11xl">
      {block?.content?.length && (
        <PortableText
          className={cn('flex flex-col items-center justify-center text-center', 'mx-auto mb-5xl')}
          tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          buttonClassName={buttonSizeClasses({ lg: 'md' })}
          value={block.content as PortableTextBlock[]}
          documentId={pageId}
          documentType={pageType}
          parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
        />
      )}

      {block?.faqItems?.length && (
        <div className="flex flex-col gap-2xl items-center md:flex-row md:gap-2xl md:justify-center md:items-stretch lg:gap-5xl">
          {block.faqItems.map((faqItemWrapper, index) => {
            if (!faqItemWrapper?.item) return null

            const faqItem = faqItemWrapper.item
            const language = faqItem.language || 'en'
            const defaultLink = language === 'es' ? FAQ_PAGES_LINK.es : FAQ_PAGES_LINK.en
            const baseHref = defaultLink.href
            const anchoredHref = `${baseHref}#${generateElementId(faqItem.question)}`
            const buttonLabel = faqItemWrapper.calloutLabel || defaultLink.label

            const calloutData: DereferencedCallout = {
              _type: 'callout',
              alignment: 'left',
              heading: faqItem.question as any,
              bgColor: 'neutral-50',
              showButton: true,
              button: {
                _type: 'button',
                size: 'sm',
                variant: 'tertiary',
                icon: {
                  _type: 'icon',
                  provider: 'fontawesome',
                  style: 'solid',
                  name: 'faAngleRight',
                },
                iconPlacement: 'right',
                link: {
                  ...defaultLink,
                  label: buttonLabel,
                  href: anchoredHref,
                },
              },
            }

            return (
              <Callout
                key={faqItemWrapper._key || faqItem._id || index}
                callout={calloutData as any}
                tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                className="max-w-[310px] [&_p]:size-subheading-md md:[&_div]:gap-lg lg:[&_div]:gap-2xl lg:[&_p]:size-subheading-lg"
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
